import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Clock, CalendarClock, ChevronRight } from 'lucide-react';
import { learningApi, LearningCourse } from '../services/api';
import { useToast } from '../components/Toast/Toast';

/** My Learning — the courses assigned to me, with how far I have got. */

export const mins = (s: number | null | undefined) => {
  const sec = Math.round(Number(s) || 0);
  if (sec < 60) return `${sec} s`; // a short video or a few seconds watched
  const m = Math.round(sec / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`;
};

const MyLearning: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [courses, setCourses] = useState<LearningCourse[] | null>(null);
  useEffect(() => {
    learningApi.my().then(r => setCourses(r.data || [])).catch(e => { showError('Could not load your courses', e?.message); setCourses([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const today = new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <GraduationCap className="h-6 w-6 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800">My Learning</h1>
        </div>
        {courses === null ? (
          <div className="text-sm text-gray-400 p-6 text-center">Loading…</div>
        ) : courses.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">No courses have been assigned to you yet.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {courses.map(c => {
              const total = Number(c.features) || 0, learned = Number(c.learned) || 0;
              const pct = total ? Math.round((learned / total) * 100) : 0;
              const done = total > 0 && learned >= total;
              const overdue = !!c.due_date && !done && c.due_date < today;
              return (
                <button key={c.id} onClick={() => navigate(`/learning/course/${c.id}`)}
                  className="text-left bg-white rounded-lg shadow hover:shadow-md transition p-4 border border-transparent hover:border-blue-200">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">{c.title}</div>
                      {c.description && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{c.description}</div>}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-300" />
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full ${done ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span className={done ? 'text-emerald-700 font-semibold' : ''}>{done ? 'Completed' : `${learned} of ${total} features learned`}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{mins(c.watched_seconds)} learnt{c.total_seconds ? ` of ${mins(c.total_seconds)}` : ''}</span>
                    {c.due_date && <span className={`inline-flex items-center gap-1 ${overdue ? 'text-red-600 font-semibold' : ''}`}><CalendarClock className="h-3.5 w-3.5" />due {c.due_date.split('-').reverse().join('-')}{overdue ? ' — overdue' : ''}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLearning;
